import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeviceCategory, VelavuAPI, VelavuDevice } from "velavu-js-api";
import { VelavuTitledModal } from "../../../elements/velavu-titled-modal";
import ModalRegisterTagDetails from "./modal-register-tag-details";
import ModalRegisterTagID, {
	ModalRegisterTagIDHandle,
} from "./modal-register-tag-id";
import * as styles from "./modal-register-tag.module.scss";

enum RegisterTagError {
	AlreadyRegistered,
	CodeError,
}

export default function ModalRegisterTag(props: {
	onClose: VoidFunction;
	onRegister: (tag: VelavuDevice) => void;
}) {
	const { t } = useTranslation();
	const tagIDInputRef = useRef<ModalRegisterTagIDHandle>(null);
	const [idInput, setIDInput] = useState("");
	const [tagDetails, setTagDetails] = useState<VelavuDevice>();
	const [isLoading, setLoading] = useState(false);
	const [error, setError] = useState<RegisterTagError | null>(null);

	const propsOnRegister = props.onRegister;
	const registerTag = useCallback(
		(tagID: string) => {
			//Updating the state
			setLoading(true);

			VelavuAPI.devices
				.getSpecificDevice(idInput)
				.catch((err) => {
					// if (err?.response?.status === 404) {
					// 	setError("");
					// } else {
					// 	setError("An error occured");
					// }
				})
				.then((resp) => {
					// If the request succeeded, then a registered device
					// already exists
					if (resp) {
						setError(RegisterTagError.AlreadyRegistered);
						throw "Already registered";
					}
					// Submitting the device ID
					return VelavuAPI.devices.registerAvailableDevice(tagID, {
						category: DeviceCategory.Tag,
					});
				})
				.then((tag: VelavuDevice) => {
					propsOnRegister(tag);

					setTagDetails(tag);
					setError(null);
				})
				.catch(() => {
					setError((current) =>
						current === null ? RegisterTagError.CodeError : current,
					);
				})
				.finally(() => {
					setLoading(false);
				});
		},
		[propsOnRegister, idInput],
	);

	const resetRegistration = useCallback(() => {
		//Clear the ID input and tag details
		setIDInput("");
		setTagDetails(undefined);
		tagIDInputRef.current?.focusInput();
	}, []);

	let errorMessage: string;
	switch (error) {
		case RegisterTagError.AlreadyRegistered:
			errorMessage = t("devices.register.already_registered");
			break;
		case RegisterTagError.CodeError:
			errorMessage = t("devices.register.code_error");
			break;
		default:
			errorMessage = "";
			break;
	}

	return (
		<VelavuTitledModal
			title={t("devices.register.title")}
			onClose={props.onClose}
		>
			<div className={styles.container}>
				<div
					style={{
						transform: tagDetails
							? "translateX(-100%)"
							: "translateX(0)",
					}}
					className={styles.contentContainer}
				>
					<div className={styles.contentSlide}>
						<ModalRegisterTagID
							ref={tagIDInputRef}
							id={idInput}
							setID={setIDInput}
							onSubmit={registerTag}
							loading={isLoading}
							error={errorMessage}
						/>
					</div>
					<div className={styles.contentSlide}>
						{tagDetails && (
							<ModalRegisterTagDetails
								tag={tagDetails}
								onRepeat={resetRegistration}
								onClose={props.onClose}
							/>
						)}
					</div>
				</div>
			</div>
		</VelavuTitledModal>
	);
}
